import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/events", label: "Events" },
  { to: "/gallery", label: "Gallery" },
  { to: "/tickets", label: "Tickets" },
  { to: "/guest-list", label: "Guest List" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border-color,padding] duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-brand-ink/10 py-3" : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/home" className="flex items-center" data-testid="nav-logo">
          <img src="/logo.png" alt="MARKIE 2 FRESH" className="h-7 md:h-8 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-brand-ink/70 hover:text-brand-orange transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/tickets"
            data-testid="nav-cta"
            className="group relative inline-flex items-center gap-2 rounded-full bg-brand-ink text-white px-6 py-2.5 text-xs font-mono uppercase tracking-[0.2em] overflow-hidden"
          >
            <span className="absolute inset-0 bg-brand-orange translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            <span className="relative">Get Tickets</span>
          </Link>
        </nav>

        <button
          className="md:hidden p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden bg-white border-b border-brand-ink/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="font-head text-2xl font-bold uppercase" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/tickets" className="mt-2 rounded-full bg-brand-orange text-white px-6 py-3 text-center font-mono text-xs uppercase tracking-[0.2em]">
                Get Tickets
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
